/**
 * Admin / use-case intake notifications
 * GET /api/v1/new-use-case-intake/admin-notifications
 */

const BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

/** Temporary hardcoded tenant — swap to session clientId later */
export const ADMIN_NOTIFICATIONS_TENANT_ID = '300';

/**
 * Map API notification → UI message shape used by NotificationsModal / MessagesContent.
 */
export function mapAdminNotificationToMessage(notification) {
  const candidateName =
    notification.candidate_name?.trim() || 'Unknown candidate';
  const matchedName = notification.matched_use_case_name?.trim() || '';
  const score =
    notification.similarity_score != null
      ? Number(notification.similarity_score)
      : null;
  const scorePct =
    score != null && !Number.isNaN(score)
      ? `${Math.round(score * 100)}% similar`
      : null;

  const decision = String(notification.resolution_decision || '').toLowerCase();
  const hasDecision = ['approved', 'rejected', 'resolved'].includes(decision);
  const isTerminalStatus = ['resolved', 'approved', 'rejected'].includes(
    String(notification.status || '').toLowerCase()
  );
  const isResolved = Boolean(notification.resolved_at) || hasDecision || isTerminalStatus;

  let title;
  let severity = 'warn';

  if (isResolved && hasDecision) {
    const decisionLabel =
      decision === 'rejected'
        ? 'Rejected'
        : decision === 'approved'
          ? 'Approved'
          : 'Resolved';
    title = matchedName
      ? `${decisionLabel}: ${candidateName} → ${matchedName}`
      : `${decisionLabel}: ${candidateName}`;
    severity = decision === 'rejected' ? 'warn' : 'success';
  } else {
    title = matchedName
      ? `Review needed: ${candidateName}`
      : `Use case review: ${candidateName}`;
  }

  const bodyParts = [];
  if (matchedName && !hasDecision) {
    bodyParts.push(`Possible match: ${matchedName}`);
  }
  if (notification.reason) {
    bodyParts.push(notification.reason);
  } else if (!isResolved) {
    bodyParts.push('Requires manual review.');
  }
  if (scorePct) {
    bodyParts.push(scorePct);
  }
  if (isResolved && notification.resolved_by) {
    bodyParts.push(`Resolved by ${notification.resolved_by}.`);
  }

  // Inbox items from status_filter=pending should appear unread unless read_at is set.
  // Do not force-read just because resolution fields are populated — API may still
  // surface them as pending.
  const read = Boolean(notification.read_at);

  return {
    id: notification.notification_id,
    severity,
    title,
    body: bodyParts.join(' '),
    createdAt: notification.created_at,
    tier: 'pro',
    read,
    category: 'Use Case Intake',
    meta: {
      notificationId: notification.notification_id,
      tenantId: notification.tenant_id,
      pendingId: notification.pending_id,
      candidateId: notification.candidate_id,
      candidateName: notification.candidate_name,
      matchedUseCaseName: notification.matched_use_case_name,
      similarityScore: notification.similarity_score,
      status: notification.status,
      resolutionDecision: notification.resolution_decision,
      resolvedBy: notification.resolved_by,
      resolvedAt: notification.resolved_at,
    },
  };
}

/**
 * Fetch admin notifications for a tenant (client).
 *
 * @param {string|number} [tenantId] - defaults to ADMIN_NOTIFICATIONS_TENANT_ID ('300')
 * @param {{ statusFilter?: string, limit?: number }} [options]
 * @returns {Promise<{
 *   success: boolean,
 *   data: Array,
 *   unresolved_count: number,
 *   total_matched: number,
 *   meta: object
 * }>}
 */
export async function fetchAdminNotifications(
  tenantId = ADMIN_NOTIFICATIONS_TENANT_ID,
  { statusFilter = 'pending', limit = 100 } = {}
) {
  const resolvedTenantId = String(
    tenantId ?? ADMIN_NOTIFICATIONS_TENANT_ID
  );

  const params = new URLSearchParams({
    tenant_id: resolvedTenantId,
    status_filter: statusFilter,
    limit: String(limit),
  });

  const token = localStorage.getItem('dva_token');
  const headers = {
    'Content-Type': 'application/json',
    'x-tenant-id': resolvedTenantId,
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(
    `${BASE}/api/v1/new-use-case-intake/admin-notifications?${params}`,
    {
      method: 'GET',
      headers,
      credentials: 'include',
    }
  );

  if (!response.ok) {
    const errBody = await response.json().catch(() => ({}));
    throw new Error(
      errBody.error || `Failed to load notifications (${response.status})`
    );
  }

  const json = await response.json();
  if (!json?.success) {
    throw new Error(json?.error || 'Failed to load notifications');
  }

  const items = Array.isArray(json.data) ? json.data : [];
  const data = items.map(mapAdminNotificationToMessage);

  return {
    success: true,
    data,
    unresolved_count: json.unresolved_count ?? data.filter((n) => !n.read).length,
    total_matched: json.total_matched ?? items.length,
    meta: {
      total: json.total_matched ?? items.length,
      unresolved: json.unresolved_count ?? data.filter((n) => !n.read).length,
      statusFilter: json.status_filter,
      tenantId: json.tenant_id,
      correlationId: json.correlation_id,
    },
  };
}

/**
 * Unresolved / unread count for the header bell badge.
 * Uses items without read_at so mark-read updates the badge.
 */
export async function fetchUnresolvedNotificationCount(
  tenantId = ADMIN_NOTIFICATIONS_TENANT_ID
) {
  const result = await fetchAdminNotifications(tenantId, {
    statusFilter: 'pending',
    limit: 100,
  });
  const unread = result.data.filter((n) => !n.read).length;
  return unread;
}

/**
 * Mark a single notification as read.
 * PATCH /api/v1/new-use-case-intake/admin-notifications/:id/mark-read
 *
 * @param {string} notificationId
 * @param {string|number} [tenantId]
 * @returns {Promise<object>} mapped notification
 */
export async function markAdminNotificationAsRead(
  notificationId,
  tenantId = ADMIN_NOTIFICATIONS_TENANT_ID
) {
  if (!notificationId) {
    throw new Error('notificationId is required');
  }

  const resolvedTenantId = String(tenantId ?? ADMIN_NOTIFICATIONS_TENANT_ID);
  const params = new URLSearchParams({ tenant_id: resolvedTenantId });

  const token = localStorage.getItem('dva_token');
  const headers = {
    'Content-Type': 'application/json',
    'x-tenant-id': resolvedTenantId,
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(
    `${BASE}/api/v1/new-use-case-intake/admin-notifications/${encodeURIComponent(notificationId)}/mark-read?${params}`,
    {
      method: 'PATCH',
      headers,
      credentials: 'include',
    }
  );

  if (!response.ok) {
    const errBody = await response.json().catch(() => ({}));
    throw new Error(
      errBody.error || `Failed to mark as read (${response.status})`
    );
  }

  const json = await response.json();
  if (!json?.success) {
    throw new Error(json?.error || 'Failed to mark as read');
  }

  return mapAdminNotificationToMessage(json.data);
}
